import {
  KeyDetailsObject,
  KeywordOrPhraseKey,
  KeywordsAndPhrasesReducerAction,
  KeywordsAndPhrasesReducerState,
  KeywordsData,
  PopoverTab,
  SingleKey,
} from './KeywordsAndPhrasesReducer.d'
import { getKeywordsState } from './utils'
import type { v5 } from '@contracts'

export enum KeywordsAndPhrasesReducerActionTypes {
  SET_KEYWORDS = 'SET_KEYWORDS',
  SET_KEYPHRASES = 'SET_KEYPHRASES',
  SET_ACTIVE_KEYWORD_OR_PHRASE = 'SET_ACTIVE_KEYWORD_OR_PHRASE',
  SET_HOVERED_KEYWORD_OR_PHRASE = 'SET_HOVERED_KEYWORD_OR_PHRASE',
  SET_CLICKED_KEYWORD_OR_PHRASE = 'SET_CLICKED_KEYWORD_OR_PHRASE',
}

const emptyKeywordObject = {
  priority: [],
  required: [],
  optional: [],
  avoid: [],
}
const emptyKeyphraseObject = {
  keyphrases_required: [],
  keyphrases_optional: [],
}

export const keywordsAndPhrasesReducerInitialState: KeywordsAndPhrasesReducerState = {
  keywords: {
    product: emptyKeywordObject,
    brand: emptyKeywordObject,
  },
  keyphrases: {
    product: emptyKeyphraseObject,
    brand: emptyKeyphraseObject,
  },
  activeKeyword: null,
  hoveredKeyword: null,
  clickedKeyword: null,
}

export const setKeywords = (payload: v5.JobsDetailData): KeywordsAndPhrasesReducerAction => ({
  type: KeywordsAndPhrasesReducerActionTypes.SET_KEYWORDS,
  payload,
})

export const setKeyphrases = (payload: v5.JobsDetailData): KeywordsAndPhrasesReducerAction => ({
  type: KeywordsAndPhrasesReducerActionTypes.SET_KEYPHRASES,
  payload,
})

export const setActiveKeyword = (payload: string | null): KeywordsAndPhrasesReducerAction => ({
  type: KeywordsAndPhrasesReducerActionTypes.SET_ACTIVE_KEYWORD_OR_PHRASE,
  payload,
})

export const setHoveredKeyword = (payload: string | null): KeywordsAndPhrasesReducerAction => ({
  type: KeywordsAndPhrasesReducerActionTypes.SET_HOVERED_KEYWORD_OR_PHRASE,
  payload,
})

export const setClickedKeyword = (payload: string | null): KeywordsAndPhrasesReducerAction => ({
  type: KeywordsAndPhrasesReducerActionTypes.SET_CLICKED_KEYWORD_OR_PHRASE,
  payload,
})

export function keywordsAndPhrasesReducer(
  state: KeywordsAndPhrasesReducerState,
  action: KeywordsAndPhrasesReducerAction,
) {
  const { type, payload } = action
  switch (type) {
    case KeywordsAndPhrasesReducerActionTypes.SET_KEYWORDS: {
      const popoverTabKeywords = getKeywordsState(payload, state, 'keyword')
      return {
        ...state,
        keywords: popoverTabKeywords,
      } as KeywordsAndPhrasesReducerState
    }
    case KeywordsAndPhrasesReducerActionTypes.SET_KEYPHRASES: {
      const popoverTabKeyphrases = getKeywordsState(payload, state, 'keyphrase')

      return {
        ...state,
        keyphrases: popoverTabKeyphrases,
      } as KeywordsAndPhrasesReducerState
    }
    case KeywordsAndPhrasesReducerActionTypes.SET_ACTIVE_KEYWORD_OR_PHRASE: {
      return {
        ...state,
        activeKeyword: payload,
      } as KeywordsAndPhrasesReducerState
    }
    case KeywordsAndPhrasesReducerActionTypes.SET_HOVERED_KEYWORD_OR_PHRASE: {
      return {
        ...state,
        hoveredKeyword: payload,
      } as KeywordsAndPhrasesReducerState
    }
    case KeywordsAndPhrasesReducerActionTypes.SET_CLICKED_KEYWORD_OR_PHRASE: {
      return {
        ...state,
        clickedKeyword: payload,
      } as KeywordsAndPhrasesReducerState
    }
    default: {
      return state
    }
  }
}

// HELPER FUNCTIONS
export function mapReducerStateToKeywordsData(state: KeywordsAndPhrasesReducerState): KeywordsData {
  const result: KeywordsData = {}
  const tabs = ['product', 'brand'] as (keyof PopoverTab<any>)[]
  const types = ['keywords', 'keyphrases'] as (keyof Pick<KeywordsAndPhrasesReducerState, 'keywords' | 'keyphrases'>)[]

  const titleMap = new Map<KeywordOrPhraseKey, string>([
    ['priority', 'Title & SEO Keywords'],
    ['required', 'Required Keywords'],
    ['optional', 'Optional Keywords'],
    ['avoid', 'Avoid'],
    ['keyphrases_required', 'Required Keyphrases'],
    ['keyphrases_optional', 'Optional Keyphrases'],
  ])

  tabs.forEach(tab => {
    result[tab] = []

    types.forEach(type => {
      const categoryData = state[type][tab]

      Object.entries(categoryData).forEach(([title, keys]) => {
        const keyDetails: KeyDetailsObject = {
          title: (titleMap.get(title as KeywordOrPhraseKey) as string) || title,
          type: type === 'keywords' ? 'keyword' : 'keyphrase',
          keys: keys.map(({ key, currentCount, originalCount }: SingleKey) => ({
            key: key,
            currentCount,
            originalCount,
          })),
        }

        result[tab].push(keyDetails)
      })
    })
  })

  return result
}
